import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getValidToken } from '../utils/authUtils';

function authHeaders() {
  const token = getValidToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : null;
}

const STAGES = ['new', 'contacted', 'engaged', 'active', 'dormant'];
const GOALS = [
  { value: 'engagement', label: 'Engagement' },
  { value: 'membership_upgrade', label: 'Membership Upgrade' },
  { value: 'donation', label: 'Donation' },
  { value: 'event_attendance', label: 'Event Attendance' },
  { value: 'board_recruitment', label: 'Board Recruitment' },
  { value: 'other', label: 'Other' },
];
const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];
const CHANNELS = ['phone', 'email', 'linkedin', 'in_person', 'text', 'other'];
const DIRECTIONS = ['outbound', 'inbound'];
const TP_OUTCOMES = ['sent', 'message_sent', 'no_answer', 'voicemail', 'connected', 'replied', 'bounced', 'opened', 'scheduled'];
const CLOSE_OUTCOMES = ['successful', 'unresponsive', 'declined', 'deferred'];

const AdminOutreachManager = () => {
  const navigate = useNavigate();

  // Access
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Data
  const [assignments, setAssignments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({});
  const [byAssignee, setByAssignee] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [stageFilter, setStageFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Staff list (for assign-to dropdown & assignee filter)
  const [staffList, setStaffList] = useState([]);

  // Stats panel
  const [showStats, setShowStats] = useState(true);

  // Research progress panel
  const [researchStats, setResearchStats] = useState(null);
  const [showResearchStats, setShowResearchStats] = useState(false);
  const [researchStatsLoading, setResearchStatsLoading] = useState(false);

  // Stage change tracking
  const [stageUpdating, setStageUpdating] = useState({});

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ goal: 'engagement', priority: 'normal', due_date: '', purpose: '' });
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [alumniSearchInput, setAlumniSearchInput] = useState('');
  const [alumniResults, setAlumniResults] = useState([]);
  const [alumniSearching, setAlumniSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  // Touchpoint modal
  const [touchpointModal, setTouchpointModal] = useState(null);
  const [touchpointForm, setTouchpointForm] = useState({ channel: 'phone', direction: 'outbound', subject: '', outcome: '', notes: '' });
  const [tpSaving, setTpSaving] = useState(false);

  // Close modal
  const [closeModal, setCloseModal] = useState(null);
  const [closeForm, setCloseForm] = useState({ outcome: 'successful', outcome_notes: '' });
  const [closeSaving, setCloseSaving] = useState(false);

  // View touchpoints modal
  const [viewTpModal, setViewTpModal] = useState(null);
  const [viewTpData, setViewTpData] = useState([]);
  const [viewTpLoading, setViewTpLoading] = useState(false);

  // ============================================
  // ACCESS CHECK
  // ============================================
  useEffect(() => {
    const check = async () => {
      const auth = authHeaders();
      if (!auth) { setAccessDenied(true); setAccessChecked(true); return; }
      try {
        const res = await axios.get(`${API_URL}/api/admin/me`, auth);
        if (res.data.is_super_admin) {
          setIsSuperAdmin(true);
        } else {
          setAccessDenied(true);
        }
      } catch {
        setAccessDenied(true);
      }
      setAccessChecked(true);
    };
    check();
  }, []);

  // ============================================
  // FETCH STAFF LIST
  // ============================================
  useEffect(() => {
    if (!accessChecked || accessDenied) return;
    const fetchStaff = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/admin-users`, authHeaders());
        const active = (res.data || []).filter(a => a.is_active !== false);
        setStaffList(active);
      } catch (err) {
        console.error('Failed to load staff list:', err);
      }
    };
    fetchStaff();
  }, [accessChecked, accessDenied]);

  // ============================================
  // FETCH ASSIGNMENTS
  // ============================================
  const fetchAssignments = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: p, page_size: 50 });
      if (stageFilter) params.set('stage', stageFilter);
      if (assigneeFilter) params.set('assignee_id', assigneeFilter);
      if (searchQuery) params.set('search', searchQuery);

      const res = await axios.get(`${API_URL}/api/exec/outreach/all-assignments?${params}`, authHeaders());
      setAssignments(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
      setTotalPages(res.data.total_pages || 1);
      setStats(res.data.stats || {});
      setByAssignee(res.data.by_assignee || []);
    } catch (err) {
      setError('Failed to load assignments: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [stageFilter, assigneeFilter, searchQuery]);

  useEffect(() => {
    if (!accessChecked || accessDenied) return;
    fetchAssignments(1);
  }, [accessChecked, accessDenied, fetchAssignments]);

  // ============================================
  // FETCH RESEARCH STATS
  // ============================================
  const fetchResearchStats = async () => {
    setResearchStatsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/contacts/assignment-stats`, authHeaders());
      setResearchStats(res.data);
    } catch (err) {
      console.error('Error fetching research stats:', err);
    } finally {
      setResearchStatsLoading(false);
    }
  };

  // ============================================
  // STAGE CHANGE
  // ============================================
  const handleStageChange = async (assignmentId, newStage) => {
    setStageUpdating(prev => ({ ...prev, [assignmentId]: true }));
    try {
      await axios.put(`${API_URL}/api/exec/outreach/assignments/${assignmentId}/stage`, { stage: newStage }, authHeaders());
      fetchAssignments(page);
    } catch (err) {
      setError('Stage update failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setStageUpdating(prev => ({ ...prev, [assignmentId]: false }));
    }
  };

  // ============================================
  // LOG TOUCHPOINT
  // ============================================
  const handleLogTouchpoint = async () => {
    if (!touchpointModal) return;
    setTpSaving(true);
    try {
      await axios.post(`${API_URL}/api/exec/outreach/assignments/${touchpointModal.id}/touchpoints`, touchpointForm, authHeaders());
      setTouchpointModal(null);
      setTouchpointForm({ channel: 'phone', direction: 'outbound', subject: '', outcome: '', notes: '' });
      fetchAssignments(page);
    } catch (err) {
      setError('Failed to log touchpoint: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTpSaving(false);
    }
  };

  // ============================================
  // CLOSE ASSIGNMENT
  // ============================================
  const handleClose = async () => {
    if (!closeModal) return;
    setCloseSaving(true);
    try {
      await axios.put(`${API_URL}/api/exec/outreach/assignments/${closeModal.id}/close`, closeForm, authHeaders());
      setCloseModal(null);
      setCloseForm({ outcome: 'successful', outcome_notes: '' });
      fetchAssignments(page);
    } catch (err) {
      setError('Failed to close assignment: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCloseSaving(false);
    }
  };

  // ============================================
  // VIEW TOUCHPOINTS
  // ============================================
  const handleViewTouchpoints = async (assignment) => {
    setViewTpModal(assignment);
    setViewTpLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exec/outreach/assignments/${assignment.id}/touchpoints`, authHeaders());
      setViewTpData(res.data.items || []);
    } catch (err) {
      console.error('Failed to load touchpoints:', err);
      setViewTpData([]);
    } finally {
      setViewTpLoading(false);
    }
  };

  // ============================================
  // ALUMNI SEARCH (for create modal)
  // ============================================
  const searchAlumni = async (query) => {
    if (!query || query.length < 2) { setAlumniResults([]); return; }
    setAlumniSearching(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/crm/contacts`, {
        ...authHeaders(),
        params: { search: query, source_filter: 'alumni', per_page: 10 }
      });
      setAlumniResults(res.data.contacts || []);
    } catch (err) {
      console.error('Alumni search failed:', err);
      setAlumniResults([]);
    } finally {
      setAlumniSearching(false);
    }
  };

  // Debounced alumni search
  useEffect(() => {
    if (!showCreateModal) return;
    const timer = setTimeout(() => {
      if (alumniSearchInput.trim().length >= 2) {
        searchAlumni(alumniSearchInput.trim());
      } else {
        setAlumniResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [alumniSearchInput, showCreateModal]);

  // ============================================
  // CREATE ASSIGNMENT
  // ============================================
  const handleCreate = async () => {
    if (!selectedAlumni || !selectedAssignee) return;
    setCreating(true);
    setCreateError('');
    setCreateSuccess('');
    try {
      const payload = {
        assignments: [{
          alumni_user_id: parseInt(selectedAlumni.id),
          assigned_to_id: parseInt(selectedAssignee),
          goal: createForm.goal || undefined,
          priority: createForm.priority || undefined,
          due_date: createForm.due_date || undefined,
          purpose: createForm.purpose || undefined,
        }]
      };
      const res = await axios.post(`${API_URL}/api/exec/outreach/assign`, payload, authHeaders());
      if (res.data.errors > 0 && res.data.error_details?.length > 0) {
        setCreateError(res.data.error_details[0].error);
      } else {
        setCreateSuccess(`Assignment created for ${selectedAlumni.first_name} ${selectedAlumni.last_name}`);
        setTimeout(() => {
          setShowCreateModal(false);
          resetCreateForm();
          fetchAssignments(1);
        }, 1200);
      }
    } catch (err) {
      setCreateError(err.response?.data?.detail || err.message);
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setSelectedAlumni(null);
    setSelectedAssignee('');
    setAlumniSearchInput('');
    setAlumniResults([]);
    setCreateForm({ goal: 'engagement', priority: 'normal', due_date: '', purpose: '' });
    setCreateError('');
    setCreateSuccess('');
  };

  // ============================================
  // RENDER HELPERS
  // ============================================
  const stageBadge = (stage) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      engaged: 'bg-purple-100 text-purple-700',
      active: 'bg-green-100 text-green-700',
      dormant: 'bg-gray-100 text-gray-600',
      closed: 'bg-gray-200 text-gray-500',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[stage] || 'bg-gray-100 text-gray-600'}`}>
        {stage ? stage.charAt(0).toUpperCase() + stage.slice(1) : 'Unknown'}
      </span>
    );
  };

  const priorityBadge = (priority) => {
    const colors = { urgent: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700' };
    if (!priority || priority === 'normal' || priority === 'low') return null;
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[priority] || ''}`}>
        {priority.toUpperCase()}
      </span>
    );
  };

  const goalLabel = (goal) => {
    const labels = { membership_upgrade: 'Membership', donation: 'Donation', event_attendance: 'Event', engagement: 'Engagement', board_recruitment: 'Board', other: 'Other' };
    return labels[goal] || goal || '-';
  };

  // ============================================
  // LOADING / ACCESS DENIED
  // ============================================
  if (!accessChecked) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>;
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <div className="text-4xl mb-4">&#128274;</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">Only superadmins can access the Outreach Manager.</p>
          <button onClick={() => navigate('/admin')} className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700">
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="text-gray-500 hover:text-gray-700 text-sm">&larr; Admin</button>
            <h1 className="text-2xl font-bold text-gray-900">Outreach Manager</h1>
            <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">Superadmin</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { resetCreateForm(); setShowCreateModal(true); }}
              className="px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-xs font-medium"
            >
              + New Outreach Assignment
            </button>
            <button
              onClick={() => { setShowResearchStats(!showResearchStats); if (!researchStats && !showResearchStats) fetchResearchStats(); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${showResearchStats ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'}`}
            >
              Research Progress
            </button>
            <button
              onClick={() => navigate('/admin/crm')}
              className="px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 text-xs font-medium"
            >
              CRM
            </button>
            <button
              onClick={() => navigate('/admin/executive-intelligence?tab=imports&section=unprovisioned')}
              className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-xs font-medium"
            >
              Research Assignments
            </button>
            <button
              onClick={() => navigate('/admin/executive-intelligence?tab=brokerage')}
              className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 text-xs font-medium"
            >
              Mentorship Requests
            </button>
            <button
              onClick={() => navigate('/admin/membership-manager')}
              className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 text-xs font-medium"
            >
              Membership
            </button>
            <button
              onClick={() => navigate('/admin/swee')}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium"
            >
              SWEE
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
            <span className="text-red-700 text-sm">{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">&times;</button>
          </div>
        )}

        {/* ============================================ */}
        {/* STATS DASHBOARD */}
        {/* ============================================ */}
        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <button
            onClick={() => setShowStats(!showStats)}
            className="w-full px-5 py-3 flex items-center justify-between text-left"
          >
            <span className="font-semibold text-gray-800">Team Outreach Stats</span>
            <span className="text-gray-400">{showStats ? '\u25B2' : '\u25BC'}</span>
          </button>

          {showStats && (
            <div className="px-5 pb-5 border-t">
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-4">
                {[
                  { label: 'Total', value: stats.total || 0, color: 'border-violet-400' },
                  { label: 'New', value: stats.new || 0, color: 'border-blue-400' },
                  { label: 'Contacted', value: stats.contacted || 0, color: 'border-yellow-400' },
                  { label: 'Engaged', value: stats.engaged || 0, color: 'border-purple-400' },
                  { label: 'Active', value: stats.active || 0, color: 'border-green-400' },
                  { label: 'Dormant', value: stats.dormant || 0, color: 'border-gray-400' },
                  { label: 'Overdue', value: stats.overdue || 0, color: 'border-red-400' },
                ].map(s => (
                  <div key={s.label} className={`bg-white border-l-4 ${s.color} rounded-lg p-3 shadow-sm`}>
                    <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* By-assignee table */}
              {byAssignee.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">By Team Member</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Team Member</th>
                          <th className="text-center py-2 px-2 font-medium text-gray-600">Total</th>
                          <th className="text-center py-2 px-2 font-medium text-blue-600">New</th>
                          <th className="text-center py-2 px-2 font-medium text-yellow-600">Contacted</th>
                          <th className="text-center py-2 px-2 font-medium text-purple-600">Engaged</th>
                          <th className="text-center py-2 px-2 font-medium text-green-600">Active</th>
                          <th className="text-center py-2 px-2 font-medium text-gray-500">Dormant</th>
                          <th className="text-center py-2 px-2 font-medium text-red-600">Overdue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byAssignee.map(s => (
                          <tr
                            key={s.admin_id}
                            className="border-b hover:bg-violet-50 cursor-pointer"
                            onClick={() => { setAssigneeFilter(String(s.admin_id)); setPage(1); }}
                          >
                            <td className="py-2 px-3 font-medium text-gray-800">{s.admin_name}</td>
                            <td className="py-2 px-2 text-center font-semibold">{s.total}</td>
                            <td className="py-2 px-2 text-center">{s.new || 0}</td>
                            <td className="py-2 px-2 text-center">{s.contacted || 0}</td>
                            <td className="py-2 px-2 text-center">{s.engaged || 0}</td>
                            <td className="py-2 px-2 text-center">{s.active || 0}</td>
                            <td className="py-2 px-2 text-center">{s.dormant || 0}</td>
                            <td className="py-2 px-2 text-center">{s.overdue > 0 ? <span className="text-red-600 font-semibold">{s.overdue}</span> : 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* RESEARCH PROGRESS DASHBOARD */}
        {/* ============================================ */}
        {showResearchStats && (
          <div className="bg-white rounded-xl shadow-sm border mb-6">
            <div className="px-5 py-3 flex items-center justify-between border-b">
              <span className="font-semibold text-emerald-800">Research Assignment Progress</span>
              <button onClick={() => setShowResearchStats(false)} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
            </div>
            <div className="px-5 pb-5">
              {researchStatsLoading && !researchStats ? (
                <div className="text-center py-6 text-gray-500 text-sm">Loading research stats...</div>
              ) : researchStats ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4 mb-4">
                    <div className="bg-indigo-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-indigo-700">{researchStats.total_assigned}</div>
                      <div className="text-xs text-indigo-600">Total Assigned</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-green-700">{researchStats.total_found}</div>
                      <div className="text-xs text-green-600">Found</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-red-700">{researchStats.total_not_found}</div>
                      <div className="text-xs text-red-600">Not Found</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-yellow-700">{researchStats.total_pending}</div>
                      <div className="text-xs text-yellow-600">Pending</div>
                    </div>
                    <div className="bg-gray-100 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-700">{researchStats.unassigned}</div>
                      <div className="text-xs text-gray-600">Unassigned</div>
                    </div>
                  </div>
                  {researchStats.by_assignee && researchStats.by_assignee.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-indigo-50 text-indigo-800">
                            <th className="px-3 py-2 text-left font-medium">Team Member</th>
                            <th className="px-3 py-2 text-center font-medium">Assigned</th>
                            <th className="px-3 py-2 text-center font-medium">Found</th>
                            <th className="px-3 py-2 text-center font-medium">Not Found</th>
                            <th className="px-3 py-2 text-center font-medium">Pending</th>
                            <th className="px-3 py-2 text-center font-medium">Overdue</th>
                            <th className="px-3 py-2 text-center font-medium">Completion</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-indigo-100">
                          {researchStats.by_assignee.map(s => (
                            <tr key={s.admin_id} className="hover:bg-indigo-50/50">
                              <td className="px-3 py-2 font-medium text-gray-800">{s.admin_name}</td>
                              <td className="px-3 py-2 text-center">{s.assigned}</td>
                              <td className="px-3 py-2 text-center text-green-700 font-medium">{s.found}</td>
                              <td className="px-3 py-2 text-center text-red-700">{s.not_found}</td>
                              <td className="px-3 py-2 text-center text-yellow-700">{s.pending}</td>
                              <td className="px-3 py-2 text-center">{s.overdue > 0 ? <span className="text-red-600 font-bold">{s.overdue}</span> : '0'}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${s.completion_pct}%` }} />
                                  </div>
                                  <span className="text-xs font-medium w-10 text-right">{s.completion_pct}%</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="mt-3 text-right">
                    <button
                      onClick={fetchResearchStats}
                      disabled={researchStatsLoading}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      {researchStatsLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">No research stats available</div>
              )}
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* ALL ASSIGNMENTS LIST */}
        {/* ============================================ */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b">
            <h2 className="font-semibold text-gray-800 mb-3">All Outreach Assignments</h2>

            {/* Stage filter pills */}
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { value: '', label: 'All' },
                { value: 'new', label: 'New' },
                { value: 'contacted', label: 'Contacted' },
                { value: 'engaged', label: 'Engaged' },
                { value: 'active', label: 'Active' },
                { value: 'dormant', label: 'Dormant' },
                { value: 'overdue', label: 'Overdue' },
              ].map(f => (
                <button
                  key={f.value}
                  onClick={() => { setStageFilter(f.value); setPage(1); }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                    stageFilter === f.value
                      ? 'bg-violet-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Assignee dropdown + Search */}
            <div className="flex flex-wrap gap-3 items-center">
              <select
                value={assigneeFilter}
                onChange={e => { setAssigneeFilter(e.target.value); setPage(1); }}
                className="border rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="">All Staff</option>
                {staffList.map(s => (
                  <option key={s.id} value={s.id}>{s.full_name || s.email}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { setSearchQuery(searchInput); setPage(1); } }}
                  placeholder="Search alumni name, email, company..."
                  className="border rounded-lg px-3 py-1.5 text-sm w-64"
                />
                <button
                  onClick={() => { setSearchQuery(searchInput); setPage(1); }}
                  className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700"
                >
                  Search
                </button>
                {searchQuery && (
                  <button
                    onClick={() => { setSearchInput(''); setSearchQuery(''); setPage(1); }}
                    className="px-2 py-1.5 text-gray-400 hover:text-gray-600 text-sm"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mr-3"></div>
                <span className="text-gray-500 text-sm">Loading assignments...</span>
              </div>
            ) : assignments.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <div className="text-3xl mb-2">&#128203;</div>
                <p className="font-medium">{stageFilter || assigneeFilter || searchQuery ? 'No assignments match this filter' : 'No active outreach assignments yet'}</p>
                <p className="text-sm text-gray-400 mt-1">Click "+ New Assignment" to create one.</p>
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-violet-50 border-b">
                    <th className="text-left py-2 px-3 font-medium text-violet-700" style={{ minWidth: 180 }}>Alumni</th>
                    <th className="text-left py-2 px-3 font-medium text-violet-700">Company</th>
                    <th className="text-left py-2 px-3 font-medium text-violet-700">Assignee</th>
                    <th className="text-left py-2 px-3 font-medium text-violet-700">Stage</th>
                    <th className="text-left py-2 px-3 font-medium text-violet-700">Goal</th>
                    <th className="text-center py-2 px-3 font-medium text-violet-700">Priority</th>
                    <th className="text-left py-2 px-3 font-medium text-violet-700">Due</th>
                    <th className="text-center py-2 px-3 font-medium text-violet-700">Touches</th>
                    <th className="text-left py-2 px-3 font-medium text-violet-700">Last Touch</th>
                    <th className="text-left py-2 px-3 font-medium text-violet-700" style={{ minWidth: 200 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map(item => (
                    <tr key={item.id} className={`border-b hover:bg-gray-50 ${item.is_overdue ? 'bg-red-50/30' : ''}`}>
                      <td className="py-2 px-3">
                        <div className="font-medium text-gray-800">{item.alumni_name}</div>
                        <div className="text-xs text-gray-500">{item.alumni_email}</div>
                        {item.linkedin_url && (
                          <a
                            href={item.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 mt-0.5"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                            LinkedIn
                          </a>
                        )}
                      </td>
                      <td className="py-2 px-3 text-gray-600">{item.alumni_company || '-'}</td>
                      <td className="py-2 px-3 text-gray-700 font-medium">{item.assigned_to_name}</td>
                      <td className="py-2 px-3">
                        <select
                          value={item.stage}
                          onChange={e => handleStageChange(item.id, e.target.value)}
                          disabled={stageUpdating[item.id]}
                          className="border rounded px-2 py-1 text-xs"
                        >
                          {STAGES.map(s => (
                            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 px-3 text-gray-600 text-xs">{goalLabel(item.goal)}</td>
                      <td className="py-2 px-3 text-center">{priorityBadge(item.priority)}</td>
                      <td className="py-2 px-3">
                        {item.due_date ? (
                          <span className={item.is_overdue ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                            {item.is_overdue && <span className="mr-1">!</span>}
                            {new Date(item.due_date + 'T00:00:00').toLocaleDateString()}
                          </span>
                        ) : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <button
                          onClick={() => handleViewTouchpoints(item)}
                          className="text-violet-600 hover:underline font-medium"
                        >
                          {item.touchpoint_count}
                        </button>
                      </td>
                      <td className="py-2 px-3 text-xs text-gray-600 whitespace-nowrap">
                        {item.last_touchpoint_at
                          ? new Date(item.last_touchpoint_at).toLocaleDateString()
                          : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setTouchpointModal(item); setTouchpointForm({ channel: 'phone', direction: 'outbound', subject: '', outcome: '', notes: '' }); }}
                            className="px-2 py-1 bg-violet-100 text-violet-700 rounded text-xs hover:bg-violet-200"
                          >
                            Log Touch
                          </button>
                          <button
                            onClick={() => { setCloseModal(item); setCloseForm({ outcome: 'successful', outcome_notes: '' }); }}
                            className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                          >
                            Close
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-5 py-3 border-t flex justify-between items-center text-sm text-gray-600">
              <span>Page {page} of {totalPages} ({total.toLocaleString()} assignments)</span>
              <div className="flex gap-2">
                <button
                  onClick={() => { setPage(p => Math.max(1, p - 1)); fetchAssignments(Math.max(1, page - 1)); }}
                  disabled={page <= 1}
                  className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  onClick={() => { setPage(p => Math.min(totalPages, p + 1)); fetchAssignments(Math.min(totalPages, page + 1)); }}
                  disabled={page >= totalPages}
                  className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* CREATE ASSIGNMENT MODAL */}
      {/* ============================================ */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Create Outreach Assignment</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Alumni search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alumni Contact *</label>
                {selectedAlumni ? (
                  <div className="border rounded-lg p-3 bg-violet-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-gray-800">{selectedAlumni.first_name} {selectedAlumni.last_name}</div>
                        <div className="text-xs text-gray-500">{selectedAlumni.email}</div>
                        {selectedAlumni.current_employer && (
                          <div className="text-xs text-gray-600 mt-1">{selectedAlumni.current_position ? `${selectedAlumni.current_position} @ ` : ''}{selectedAlumni.current_employer}</div>
                        )}
                        {selectedAlumni.membership && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-violet-200 text-violet-700 text-[10px] rounded-full font-medium">{selectedAlumni.membership}</span>
                        )}
                        {selectedAlumni.outreach && (
                          <div className="mt-1 text-xs text-orange-600 font-medium">Has active outreach (stage: {selectedAlumni.outreach})</div>
                        )}
                      </div>
                      <button onClick={() => { setSelectedAlumni(null); setAlumniSearchInput(''); }} className="text-gray-400 hover:text-gray-600">&times;</button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={alumniSearchInput}
                      onChange={e => setAlumniSearchInput(e.target.value)}
                      placeholder="Search by name, email, or company..."
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      autoFocus
                    />
                    {alumniSearching && <div className="absolute right-3 top-2.5 text-xs text-gray-400">Searching...</div>}
                    {alumniResults.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {alumniResults.map(c => (
                          <button
                            key={c.id}
                            onClick={() => {
                              setSelectedAlumni(c);
                              setAlumniResults([]);
                              setAlumniSearchInput('');
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b last:border-b-0 text-sm"
                          >
                            <div className="font-medium">{c.first_name} {c.last_name}</div>
                            <div className="text-xs text-gray-500">
                              {c.current_position && `${c.current_position} @ `}{c.current_employer || ''}{c.email ? ` | ${c.email}` : ''}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Assign To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To *</label>
                <select
                  value={selectedAssignee}
                  onChange={e => setSelectedAssignee(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select staff member...</option>
                  {staffList.map(s => (
                    <option key={s.id} value={s.id}>{s.full_name || s.email}</option>
                  ))}
                </select>
              </div>

              {/* Goal + Priority (side by side) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Goal</label>
                  <select
                    value={createForm.goal}
                    onChange={e => setCreateForm(f => ({ ...f, goal: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    {GOALS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={createForm.priority}
                    onChange={e => setCreateForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={createForm.due_date}
                  onChange={e => setCreateForm(f => ({ ...f, due_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Purpose */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                <textarea
                  value={createForm.purpose}
                  onChange={e => setCreateForm(f => ({ ...f, purpose: e.target.value.slice(0, 500) }))}
                  placeholder="Why is this person being reached out to?"
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                <div className="text-xs text-gray-400 text-right">{createForm.purpose.length}/500</div>
              </div>

              {/* Errors / Success */}
              {createError && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{createError}</div>}
              {createSuccess && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">{createSuccess}</div>}
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm">Cancel</button>
              <button
                onClick={handleCreate}
                disabled={!selectedAlumni || !selectedAssignee || creating}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* LOG TOUCHPOINT MODAL */}
      {/* ============================================ */}
      {touchpointModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Log Touchpoint</h3>
              <p className="text-sm text-gray-500">For {touchpointModal.alumni_name}</p>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Channel</label>
                  <select value={touchpointForm.channel} onChange={e => setTouchpointForm(f => ({ ...f, channel: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm">
                    {CHANNELS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1).replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Direction</label>
                  <select value={touchpointForm.direction} onChange={e => setTouchpointForm(f => ({ ...f, direction: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm">
                    {DIRECTIONS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
                <input type="text" value={touchpointForm.subject} onChange={e => setTouchpointForm(f => ({ ...f, subject: e.target.value }))} placeholder="e.g., Intro call, Follow-up email..." className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Outcome</label>
                <select value={touchpointForm.outcome} onChange={e => setTouchpointForm(f => ({ ...f, outcome: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm">
                  <option value="">Select...</option>
                  {TP_OUTCOMES.map(o => <option key={o} value={o}>{
                    o === 'sent' ? 'Sent Email' :
                    o === 'message_sent' ? 'Sent Direct Message' :
                    o.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                  }</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea value={touchpointForm.notes} onChange={e => setTouchpointForm(f => ({ ...f, notes: e.target.value }))} rows={3} placeholder="Details..." className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button onClick={() => setTouchpointModal(null)} className="px-4 py-2 text-gray-600 text-sm">Cancel</button>
              <button onClick={handleLogTouchpoint} disabled={tpSaving} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700 disabled:opacity-50">
                {tpSaving ? 'Saving...' : 'Log Touchpoint'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* CLOSE ASSIGNMENT MODAL */}
      {/* ============================================ */}
      {closeModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Close Assignment</h3>
              <p className="text-sm text-gray-500">Close the outreach for <span className="font-medium">{closeModal.alumni_name}</span></p>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Outcome</label>
                <select value={closeForm.outcome} onChange={e => setCloseForm(f => ({ ...f, outcome: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm">
                  {CLOSE_OUTCOMES.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea value={closeForm.outcome_notes} onChange={e => setCloseForm(f => ({ ...f, outcome_notes: e.target.value }))} rows={3} placeholder="Summary of outcome..." className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button onClick={() => setCloseModal(null)} className="px-4 py-2 text-gray-600 text-sm">Cancel</button>
              <button onClick={handleClose} disabled={closeSaving} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50">
                {closeSaving ? 'Closing...' : 'Close Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* VIEW TOUCHPOINTS MODAL */}
      {/* ============================================ */}
      {viewTpModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Touchpoint History</h3>
                <p className="text-sm text-gray-500">{viewTpModal.alumni_name}</p>
              </div>
              <button onClick={() => setViewTpModal(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="px-6 py-4">
              {viewTpLoading ? (
                <div className="py-8 text-center text-gray-500">Loading touchpoints...</div>
              ) : viewTpData.length === 0 ? (
                <div className="py-8 text-center text-gray-400">No touchpoints logged yet.</div>
              ) : (
                <div className="space-y-3">
                  {viewTpData.map(tp => (
                    <div key={tp.id} className="border rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs font-medium">
                          {tp.channel?.replace('_', ' ')}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                          {tp.direction}
                        </span>
                        {tp.outcome && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                            {tp.outcome?.replace('_', ' ')}
                          </span>
                        )}
                        <span className="text-xs text-gray-400 ml-auto">
                          {tp.contacted_at ? new Date(tp.contacted_at).toLocaleString() : ''}
                        </span>
                      </div>
                      {tp.subject && <div className="text-sm font-medium text-gray-700">{tp.subject}</div>}
                      {tp.notes && <div className="text-sm text-gray-600 mt-1">{tp.notes}</div>}
                      {tp.performed_by_name && <div className="text-xs text-gray-400 mt-1">by {tp.performed_by_name}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOutreachManager;
